<template>
  <div>
    <OnBoardingStepIndicator v-if="inSignupWizard" :current="1" />

    <div class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('shared.back') }}</span>
    </div>

    <h2 class="auth-heading">{{ $t('on_boarding.email_sign_up_details_page.create_your_account') }}</h2>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <form @submit.prevent="signUp">
      <div class="pro-field mb-3">
        <label class="pro-field__label" for="signupEmail">{{ $t('shared.email') }}</label>
        <input
          id="signupEmail"
          v-model.trim="email"
          type="email"
          class="form-control form-control-lg pro-field__input"
          placeholder="example@email.com"
          autocomplete="email"
          required
        >
      </div>

      <!-- Single password field with show/hide toggle. We've intentionally
           dropped the confirm-password field — show/hide solves the typo
           problem with less friction. -->
      <div class="pro-field mb-4">
        <label class="pro-field__label" for="signupPassword">{{ $t('on_boarding.email_sign_up_details_page.password') }}</label>
        <div class="password-input">
          <input
            id="signupPassword"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="form-control form-control-lg pro-field__input"
            :placeholder="$t('on_boarding.email_sign_up_details_page.at_least_6_characters')"
            autocomplete="new-password"
            minlength="6"
            required
          >
          <button
            type="button"
            class="password-toggle"
            :aria-label="showPassword ? $t('on_boarding.email_sign_up_details_page.hide_password') : $t('on_boarding.email_sign_up_details_page.show_password')"
            @click="showPassword = !showPassword"
          >
            <span class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
          </button>
        </div>
      </div>

      <button
        type="submit"
        class="btn pro-btn pro-btn--accent w-100"
        :disabled="isLoading || !canSubmit"
      >
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>{{ $t('on_boarding.email_sign_up_details_page.creating_account') }}</span>
        </template>
        <template v-else>
          <span class="material-icons">check_circle</span>
          <span>{{ $t('on_boarding.email_sign_up_details_page.create_account') }}</span>
        </template>
      </button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';

export default defineComponent({
  name: 'OnBoardingEmailSignUpDetailsPage',
  components: { OnBoardingStepIndicator },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
    };
  },
  computed: {
    // An interruption flow (a "like" or "add to list" signup) reaches this form
    // too, and runs no profile steps afterwards — so there is no progress to
    // show there.
    inSignupWizard(): boolean {
      return this.$store.getters['onBoarding/isDeliberateSignup'];
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    canSubmit(): boolean {
      return this.email.length > 0 && this.password.length >= 6;
    },
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
    async signUp() {
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.email_sign_up_details_page.fill_in_all_fields'));
        return;
      }
      // No name here any more — the profile steps ask for first and last name
      // immediately after, matching the mobile flow (email → password → name).
      await this.$store.dispatch('onBoarding/signUpWithEmail', {
        email: this.email,
        password: this.password,
      });
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #a1a4ad;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover {
  color: #fff;
}

.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 1.25rem;
}

/* Password show/hide affordance — sits inside the rounded `pro-field__input`. */
.password-input {
  position: relative;
}
.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  border-radius: 8px;
}
.password-toggle:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}
.password-toggle .material-icons {
  font-size: 20px;
}
</style>
